<template>
    <modal v-model="showModal">
        <template #title>
            <div v-show="!loading">
                <i class="fas fa-building mr-5"></i>
                {{ title }}
            </div>
        </template>

        <preloader v-if="loading"></preloader>

        <div v-else class="mx-auto flex md:space-x-6 md:flex-row flex-col space-y-7 md:space-y-0">
            <!-- Left Section (Contact Info) -->
            <div class="md:w-2/5 flex flex-col items-center text-center">
                <i class="fa fa-duotone fa-3x" :class="booking.contact.type === 'individual' ? 'fa-user' : 'fa-building'"></i>

                <h3>
                    <span v-if="booking.contact && canSeeContact">
                        {{ contactName }}
                        <a :href="booking.contact.url" target="_blank">
                            <i class="fa-solid fa-link text-md text-primary"></i>
                        </a>
                    </span>
                    <span v-else>{{ contactName }}</span>
                </h3>

                <div v-if="booking.contact.email" class="truncate text-muted w-full">
                    <i class="fa-envelope fa-solid mr-2"></i>
                    <a :href="`mailto:${booking.contact.email}`" class="text-muted">
                        {{ booking.contact.email }}
                    </a>
                </div>

                <div v-if="booking.contact.phone" class="text-muted mt-2">
                    <i class="fa-phone fa-solid mr-2"></i>
                    <a :href="`tel:${booking.contact.phone}`" class="text-muted">
                        {{ booking.contact.phone }}
                    </a>
                </div>

                <div v-if="canSeeContact" class="mt-6 space-y-4 w-full">
                    <ep-button expand start-icon="pencil" @click="editContactBooking">{{ trans('calendar.edit_other_booking_heading') }}</ep-button>

                    <ep-button v-if="showCreateInvoiceButton" variant="secondary" expand start-icon="file-plus"  @click="createNewInvoice()">
                        <span class="m-l-5 client-control-button-label">{{ trans('invoices.invoice_create') }}</span>
                    </ep-button>
                </div>
            </div>

            <!-- Right Section (Appointment Info) -->
            <div class="md:w-3/5 flex flex-col space-y-6 md:border-l border-solid border-0 border-grey-200 border-t md:border-t-0 pt-6">
                <!-- Appointment Info -->
                <div class="flex flex-col items-center text-grey-700">
                    <i class="fa-regular fa-clock text-3xl mb-5"></i>
                    <p v-sanitize-html="$filters.datetime_span_to(booking.start, booking.end)" class="text-lg font-medium"></p>
                    <p class="text-base font-medium">{{ booking.title }}</p>
                    <span class="text-sm">
                        <i class="fa-solid fa-circle text-xs" :style="{color: booking.calendar_color}"></i>
                        {{ booking.calendar_name }}
                    </span>
                </div>

                <!-- Notes -->
                <div v-if="booking.notes" class="flex flex-col text-grey-700 border-t border-solid border-0 border-grey-200 px-6 pt-6">
                    <expandable-text :escape="true" :content="booking.notes"></expandable-text>
                </div>

                <!-- Boxes on the right side below the main information -->
                <info-box :items="items" />
            </div>
        </div>
    </modal>
</template>

<script>
import { mapGetters } from 'vuex';
import { truncate } from 'lodash';
import { http } from '@/system/http';
import Modal from '@/system/components/global/Modal';
import Preloader from '@/system/components/global/Preloader';
import InfoBox from '@/system/components/apps/other_payers/bookings/InfoBox';
import ExpandableText from '@/system/components/global/ExpandableText';

export default {
    components: {
        Modal,
        InfoBox,
        Preloader,
        ExpandableText,
    },

    data() {
        return {
            loading: true,
            showModal: false,
            booking: {
                contact: {},
                practitioners: [],
            },
        };
    },

    computed: {
        ...mapGetters('auth', ['isOwner']),

        title() {
            return this.trans('apps.other_payers.contact_bookings.booking_with', { name: this.contactName });
        },

        calendars() {
            return this.$store.getters['calendars/getCalendars']();
        },

        contactName() {
            if (!this.booking?.contact) {
                return this.trans('clients.deleted_client');
            }

            return this.canSeeContactName
                ? this.booking.contact.name
                : truncate(this.trans('clients.unknown_client'), { length: 50 });
        },

        canSeeContact() {
            return this.$can('see', 'client', this.booking.contact);
        },

        canEditInvoices() {
            /**
             * not using "edit-invoicesassigned" for now
             * because a contact cannot be assigned to a practitioner yet
             */
            return this.$can('edit-invoices');
        },

        canSeeInvoices() {
            return this.$can('see-invoices');
        },

        showCreateInvoiceButton() {
            if (this.booking?.contact && this.canSeeContact && this.canEditInvoices) {

                if (!this.booking.is_fully_invoiced) {
                    return true;
                }

                const invoices = this.booking.related_invoices ?? [];
                if (invoices.length === 0) {
                    /**
                     * The booking is fully invoiced without any related invoice.
                     * The booking must be an imported and paid using memos.
                     */
                    return false;
                }

                if (invoices.every(invoice => invoice.status === 'settled')) {
                    /**
                     * If every related invoice is credited, we allow invoice creation.
                     */
                    return true;
                }
            }

            return false;
        },


        canSeeClient() {
            return this.$can('see', 'client', this.booking.client);
        },

        canSeeContactName() {
            return this.canSeeUnassignedContactNames
                || this.booking.contact && this.canSeeContact;
        },

        canSeeUnassignedContactNames() {
            return this.$store.state.auth.user?.can_see_unassigned_client_names;
        },

        items () {
            const items = [];

            // 0. Related invoices
            if (this.invoicesInfoBox) {
                items.push(this.invoicesInfoBox)
            }

            // 1. practitioners[0]
            if (this.booking?.practitioners?.length) {
                items.push({
                    imageUrl: this.booking.practitioners[0].profile_image_url,
                    title: this.booking.practitioners[0].realname,
                    description: this.$store.getters['practitioners/label'],
                });
            }

            // 2. Client
            if ( this.canSeeClient && this.booking?.client) {
                items.push({
                    imageUrl: this.booking.client.profile_image_url,
                    title: this.booking.client.name,
                    description: this.trans('apps.other_payers.contact_bookings.associated_client'),
                    link: `/app/client/${this.booking.client.id}`,
                    linkText: this.trans('clients.goto'),
                });
            }

            // 3. Blocked by another calendar
            if (this.calendarBlockingLabel) {
                items.push({
                    icon: 'fa-duotone fa-calendar-circle-minus',
                    title: this.trans('calendar_blocking.blocking') ,
                    description: this.calendarBlockingLabel,
                    triggerEventText: this.trans('sendables.view_more'),
                    triggerEvent: () => this.$event_bus.$emit('showContactBookingBlockedCalendarsOverviewModal', this.booking),
                });
            }

            return items;
        },

        invoicesInfoBox () {
            if (!this.booking.is_fully_invoiced) {
                return null;
            }

            const relatedInvoices = this.booking.related_invoices;

            let title = this.trans('global.invoices');
            let description = this.trans('calendar.client_booking_invoiced');

            if (relatedInvoices.length === 1) {
                /**
                 * if there is 1 invoice then the title should be "Invoice - <Invoice Number>"
                 */
                const invoice = relatedInvoices[0];
                title = `${this.trans('global.invoice')} ${invoice.number}`;

                /**
                 * Now if there is only one related invoice and it's status is "settled" then we show "status_formatted" at the end of the title
                 * Also set the description to empty string
                 */
                if (invoice.status === 'settled') {
                    title += ` (${invoice.status_formatted})`;
                    description = '';
                }
            }

            let links = [];

            if (this.canSeeInvoices) {
                /**
                 * If there are multiple relatedInvoices, we need to pass links array to the items array
                 * 1. The title of a draft invoice should be "Go to Invoice (Invoice Draft)"
                 * 2. The title of a normal invoice should be "Go to Invoice - <Invoice Number>"
                 */
                links = relatedInvoices.map(({ id, draft, number, status, status_formatted }) => {
                    const baseTitle = this.trans('calendar.booking_goto_invoice'); // Go to Invoice

                    const statusFormatted = ['settled', 'draft'].includes(status) ? `(${status_formatted})` : '';
                    const invoiceTitle = statusFormatted || draft ? statusFormatted : number;

                    return {
                        link: `/app/invoice/${id}`,
                        enableLinks: this.canSeeInvoices,
                        title: relatedInvoices.length === 1 ? baseTitle : `${baseTitle} ${invoiceTitle}`,
                    };
                });
            }

            return {
                icon: 'fa-duotone fa-file-text',
                title,
                description,
                links,
            }
        },

        calendarBlockingLabel () {
            const calendarIds = this.booking?.calendar_ids_this_contact_booking_is_blocking_collection

            if (!calendarIds?.length) {
                return false;
            }

            const active_blocking_calendar_ids = calendarIds.filter(calendar_blocking => {
                return !calendar_blocking.has_exception
                    && this.$store.getters['calendars/getCalendarById'](calendar_blocking.calendar_id)
            });

            /**
             * The user might not have access to the calendar which is blocked so
             * check if at least one calendar id exists in the store this.$store.getters['calendars/getCalendarById']
             **/
            if (!this.isOwner) {
                const hasActiveBlockingCalendar = calendarIds.some(calendar_blocking => {
                    return this.$store.getters['calendars/getCalendarById'](calendar_blocking.calendar_id)
                });

                if (!hasActiveBlockingCalendar) {
                    return false;
                }
            }

            switch (active_blocking_calendar_ids.length) {
                case 0:
                    return this.trans('calendar_blocking.booking_block_tile_0');
                case 1:
                    return this.trans('calendar_blocking.booking_block_tile_1', {
                        calendar1: this.$store.getters['calendars/getCalendarById'](calendarIds[0].calendar_id).name
                    });
                case 2:
                    return this.trans('calendar_blocking.booking_block_tile_2', {
                        calendar1: this.$store.getters['calendars/getCalendarById'](calendarIds[0].calendar_id).name,
                        calendar2: this.$store.getters['calendars/getCalendarById'](calendarIds[1].calendar_id).name
                    });
                default:
                    return this.trans('calendar_blocking.booking_block_tile_many', {
                        count: calendarIds.length
                    });
            }
        }
    },

    watch: {
        showModal (newValue) {
            if (!newValue) {
                this.reset();
            }
        },
    },

    mounted() {
        this.showContactBookingEventHandler();
        this.hideContactBookingEventHandler();
    },

    methods: {
        showContactBookingEventHandler() {
            this.$event_bus.$on('showContactBookingModal', (booking, reloadCalendars = false) => {
                this.booking.id = booking.id;
                this.showModal = true;
                this.loadContactBooking();
                if (reloadCalendars) {
                    this.$event_bus.$emit('calendarReload');
                }
            });
        },

        hideContactBookingEventHandler() {
            this.$event_bus.$on('hideContactBookingModal', () => {
                this.showModal = false;
            });
        },

        async loadContactBooking() {
            try {
                const { data } = await http.get(`/app/contacts/bookings/${this.booking.id}`)
                this.booking = data;
            } finally {
                this.loading = false;
            }
        },

        reset() {
            this.booking = {
                contact: {},
            };
            this.loading = true;
        },

        editContactBooking() {
            this.$event_bus.$emit('editContactBooking', this.booking);
        },

        createNewInvoice() {
            /**
             * take the user to /app/invoice?bid=booking.id
             */

            const invoiceUrl = '/app/invoice';

            const queryParam = {
                contact: this.booking.contact.id,
                bid: this.booking.id,
            }

            window.location.href = `${invoiceUrl}?${new URLSearchParams(queryParam).toString()}`;
        },
    },
};
</script>
